/**
 * `/czat` — the inbox and one conversation (`pages/chat`).
 *
 * `?c=` names the open conversation and `?clip=` a client's recording playing over it. `?klient=`
 * names a CLIENT instead — what the Pulpit's „Odpisz" knows — and is replaced by their conversation's
 * `?c=` once it is found or opened, so the address never keeps both. Choosing a
 * conversation REPLACES the entry, so Back leaves the chat rather than walking every conversation
 * the trainer looked at; opening a recording PUSHES, so Back closes the player first.
 */

import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';

import { ChatScreen } from '~/pages/chat';

interface ChatSearch {
  c?: number;
  clip?: number;
  klient?: number;
}

function positiveInt(value: unknown): number | undefined {
  const parsed = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN;
  return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
}

export const Route = createFileRoute('/_shell/czat')({
  validateSearch: (raw: Record<string, unknown>): ChatSearch => {
    const c = positiveInt(raw.c);
    const clip = positiveInt(raw.clip);
    const klient = positiveInt(raw.klient);
    return {
      ...(c === undefined ? {} : { c }),
      ...(c === undefined || clip === undefined ? {} : { clip }),
      ...(c !== undefined || klient === undefined ? {} : { klient }),
    };
  },
  component: ChatRoute,
});

function ChatRoute() {
  const { c, clip, klient } = useSearch({ from: '/_shell/czat' });
  const navigate = useNavigate();
  return (
    <ChatScreen
      conversationId={c ?? null}
      clipId={clip ?? null}
      customerId={klient ?? null}
      onOpen={(conversationId) => {
        void navigate({
          to: '/czat',
          search: conversationId === null ? {} : { c: conversationId },
          replace: true,
        });
      }}
      onClip={(clipId) => {
        void navigate({
          to: '/czat',
          search: (previous) => ({
            ...(previous.c === undefined ? {} : { c: previous.c }),
            ...(clipId === null ? {} : { clip: clipId }),
          }),
          replace: clipId === null,
        });
      }}
    />
  );
}
